'use client'

import * as React from "react";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { Dictionary } from "@/types/dictionary";
import { BookingSubject } from "@/types/calendar";
import {
  bookMeeting,
  getAvailableTimes,
  getNextAvailableDate,
  prefetchAvailableTimes,
  invalidateAvailableTimes,
} from "@/lib/services/calendar";
import { useRouterManager } from "@/lib/router-manager";
import Loader from "../loader";
import BookingProgress from "./booking-progress";
import { ClientDetailsForm, BookingFormValues } from "./booking-form";
import { BookingDetails } from "@/types/booking";
import { sendGTMEvent } from "@next/third-parties/google";
import { reportGoogleAdsLead } from "@/lib/analytics/google-ads";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useLang } from "@/app/context/LangContext";

export default function BookingCalendar({
  dict = {},
  initialSubject,
  leadData,
  utmData,
  landingPageSlug,
  lang,
  embedded = false,
}: {
  dict?: Partial<Dictionary["calendar"]>
  initialSubject?: BookingSubject
  leadData?: any
  utmData?: Record<string, string>
  landingPageSlug?: string
  lang?: string
  // When rendered inside another page (e.g. the AI Blueprint result), drop the
  // full-viewport `min-h-screen` centering + large top margin that the
  // standalone booking page uses - otherwise it adds a big empty band of
  // vertical space above the card.
  embedded?: boolean
}) {
  const {
    consultationTitle = "Consultation with Palmidos",
    docbrainTitle = "DocBrain AI",
    subtitle = "Check out our availability and book the date and time that works for you",
    selectDateTime = "Select a Date and Time",
    noAvailability = "No availability on",
    checkNextAvailability = "Check Next Availability",
    clientDetailsTitle = "Client Details",
    labels = {
      subject: "Subject",
      name: "Name *",
      email: "Email *",
      phone: "Phone Number *",
      message: "Message *",
    },
    bookingDetailsTitle = "Booking Details",
    onlineMeeting = "Online Google Meet meeting",
    duration = "30 min",
    availableOnline = "Available Online",
  } = dict || {}

  const routerManager = useRouterManager()
  const { trackEvent, trackConversion, identifyLead } = useAnalytics()
  // The standalone /booking page doesn't pass `lang`; fall back to the layout's
  // language context so Hebrew visitors get Hebrew copy there too.
  const contextLang = useLang()
  const resolvedLang = lang || contextLang
  const onDateChosen = (date: Date | undefined) => {
    if (date) {
      trackEvent('calendar_date_selected', {
        date_iso: date.toISOString().split('T')[0],
        time_zone: timeZone,
        meeting_type: meetingType,
      });
    }
    setSelectedDate(date);
  };
  const onTimeChosen = (time: string) => {
    trackEvent('calendar_time_selected', {
      time,
      date_iso: selectedDate ? selectedDate.toISOString().split('T')[0] : null,
      meeting_type: meetingType,
    });
    setSelectedTime(time);
  };
  const searchParams = useSearchParams()

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    searchParams?.get("time") || undefined
  )
  const [availableTimes, setAvailableTimes] = useState<string[]>([])
  const [availabilityError, setAvailabilityError] = useState(false)
  const [nextAvailable, setNextAvailable] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isBooking, setIsBooking] = useState(false)
  const [slotTakenMessage, setSlotTakenMessage] = useState<string | null>(null)
  const [meetingType] = useState<BookingSubject>(
    initialSubject || (searchParams?.get("subject") as BookingSubject)
  )
  const [timeZone, setTimeZone] = useState("")

  // The browser's timezone and "tomorrow" exist only client-side. Initializing
  // them during render made the server HTML say "UTC" where the client's first
  // render said "Asia/Jerusalem" - React threw #425/#422 on production
  // (Vercel renders in UTC), discarded the whole tree and re-rendered it from
  // scratch, which is why the page flashed blank. Set them after mount so the
  // server and client render the same neutral shell.
  useEffect(() => {
    setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    setSelectedDate(tomorrow)
  }, [])

  // Pull the slots for whichever date is selected. Extracted from the mount
  // effect so the 409 handler can re-run it after a slot is taken.
  const refreshAvailableTimes = React.useCallback(async () => {
    if (!selectedDate || !timeZone) return
    try {
      setIsLoading(true)
      setAvailabilityError(false)
      const formattedDate = format(selectedDate, "yyyy-MM-dd")
      const data = await getAvailableTimes(formattedDate, timeZone)
      setAvailableTimes(
        selectedDate.getDay() === 5 || selectedDate.getDay() === 6 ? [] : data
      )
    } catch (err) {
      // "Couldn't load availability" must not render as "no availability" -
      // that dead-ends the lead with no retry and hides an outage from us.
      console.error("Error fetching available times:", err)
      setAvailableTimes([])
      setAvailabilityError(true)
    } finally {
      setIsLoading(false)
    }
  }, [selectedDate, timeZone])

  useEffect(() => {
    if (!selectedDate || !timeZone) return
    setSelectedTime(undefined) // Reset selected time when date changes
    setSlotTakenMessage(null)
    refreshAvailableTimes()

    // Warm the next weekday too: leads who don't like today's slots almost
    // always click the following day, and by then it's already loaded.
    const next = new Date(selectedDate)
    do {
      next.setDate(next.getDate() + 1)
    } while (next.getDay() === 5 || next.getDay() === 6)
    prefetchAvailableTimes(format(next, "yyyy-MM-dd"), timeZone)
  }, [selectedDate, timeZone, refreshAvailableTimes])

  useEffect(() => {
    if (!selectedDate || !timeZone) return setNextAvailable(null)
    if (availabilityError) return setNextAvailable(null)

    const fetchNextAvailableDate = async () => {
      if (
        selectedDate.getDay() === 5 ||
        selectedDate.getDay() === 6 ||
        availableTimes.length === 0
      ) {
        try {
          setIsLoading(true)
          const formattedDate = format(selectedDate, "yyyy-MM-dd")
          const data = await getNextAvailableDate(formattedDate, timeZone)
          setNextAvailable(data?.nextAvailableDate || null)
        } catch {
          setNextAvailable(null)
        } finally {
          setIsLoading(false)
        }
      } else {
        setNextAvailable(null)
      }
    }

    fetchNextAvailableDate()
  }, [selectedDate, availableTimes.length, timeZone, availabilityError])

  const handleSubmitForm = async (data: BookingFormValues) => {
    if (!selectedDate || !selectedTime) {
      console.error('Date or time not selected', { selectedDate, selectedTime });
      return;
    }

    setIsBooking(true)
    const bookingDateTime = new Date(selectedDate)
    const [hours, minutes] = selectedTime.split(":" ).map(Number)
    bookingDateTime.setHours(hours, minutes, 0, 0)

    const bookingDetails: BookingDetails = {
      subject: meetingType,
      ...data,
      date: bookingDateTime.toISOString().split("T")[0],
      time: selectedTime,
      userTimeZone: timeZone,
      meetingType: meetingType,
      lang: resolvedLang,
    }

    // Identify and count the attempt BEFORE the request goes out. Firing these
    // after the await meant a request that threw (network drop, timeout) was
    // never counted as submitted, so real failures stayed invisible in the funnel.
    const isLandingPageBooking = Boolean(leadData && landingPageSlug)
    identifyLead(data.email, {
      name: data.name,
      phone: data.phone,
      lead_source: isLandingPageBooking ? 'landing_page_booking' : 'booking_form',
      meeting_type: meetingType,
      last_slot_iso: bookingDateTime.toISOString(),
      ...(isLandingPageBooking ? { lp_slug: landingPageSlug } : {}),
    });
    trackEvent('booking_submitted', {
      subject: meetingType,
      slot_iso: bookingDateTime.toISOString(),
      time_zone: timeZone,
      lang: resolvedLang,
      source: isLandingPageBooking ? 'landing_page' : 'site_booking',
      ...(isLandingPageBooking ? { slug: landingPageSlug } : {}),
    });

    try {
      let response;

      // If we have lead data (from landing page), use the landing page booking API
      if (isLandingPageBooking) {
        const fullBookingData = {
          // Booking details
          date: bookingDetails.date,
          time: selectedTime,
          timeZone: timeZone,
          subject: meetingType,
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message || '',
          acceptedPrivacyPolicy: data.acceptedPrivacyPolicy,
          
          // Lead context
          firstName: leadData.firstName,
          budget: leadData.budget,
          challenge: leadData.challenge,
          
          // System fields
          lang: lang,
          landingPageSlug: landingPageSlug,
          
          // UTM parameters
          ...utmData,
        };
        
        response = await fetch('/api/landing-page/book', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(fullBookingData),
        });

        // Only a confirmed booking is a conversion: this used to fire for
        // 409s and 500s too, so Meta's `booking`/`Schedule` counted failed
        // attempts as meetings.
        if (response.ok) {
          sendGTMEvent({
            event: "book_meeting",
            eventData: {
              send_to: 'AW-16923196898/BO6wCOXM-68aEOL7zYU_',
              value: 1.0,
              currency: 'ILS',
              component: 'paid',
            },
          });
          reportGoogleAdsLead();
        }
      } else {
        // Use the regular booking API
        response = await bookMeeting(bookingDetails, 'booking-form');
      }

      if (!response.ok) {
        trackEvent('booking_failed', {
          source: isLandingPageBooking ? 'landing_page' : 'site_booking',
          status: response.status,
          slot_iso: bookingDateTime.toISOString(),
          lang: resolvedLang,
        });

        // 409 means only that this slot went while the lead was filling in the
        // form - the booking itself is still perfectly possible. Sending them
        // to the failure page for that lost a lead who was one tap from
        // converting, so instead we stay put, say what happened, and refresh
        // the times for the date they're already looking at.
        if (response.status === 409) {
          invalidateAvailableTimes(bookingDetails.date, timeZone)
          setSelectedTime(undefined)
          setSlotTakenMessage(slotTakenText)
          await refreshAvailableTimes()
          return
        }
      }

      // ChatGPT Ads conversion: only a confirmed booking counts, same rule as
      // the GTM `book_meeting` event above - and this branch covers the site
      // booking path too, not just the landing page one.
      if (response.ok) {
        trackConversion('appointment_scheduled');
      }

      const path = response.ok
        ? `${routerManager.BOOKING_SUCCESS_PAGE_PATH}?date=${bookingDetails.date}&time=${selectedTime}&timeZone=${timeZone}&name=${encodeURIComponent(data.name)}`
        : `${routerManager.BOOKING_FAILED_PAGE_PATH}`
      routerManager.goTo(path)
    } catch (error) {
      console.error('[booking] Request threw before a response was received', error);
      trackEvent('booking_failed', {
        source: isLandingPageBooking ? 'landing_page' : 'site_booking',
        slot_iso: bookingDateTime.toISOString(),
        lang: resolvedLang,
        error_message: error instanceof Error ? error.message : 'unknown',
      });
      routerManager.goTo(`${routerManager.BOOKING_FAILED_PAGE_PATH}`)
    } finally {
      setIsBooking(false)
    }
  }

  // ── Copy for the booking wait ─────────────────────────────────────────────
  // The step timings track what the server really does in that window: the
  // agent's round trip first, then the calendar write, then the confirmations.
  // The last one holds if the wait runs long, so the overlay never claims to
  // have finished something it hasn't.
  const isHe = resolvedLang === 'he'
  const bookingSteps = isHe
    ? [
        { at: 0, label: 'תופסים לך את השעה…' },
        { at: 1500, label: 'מוסיפים את הפגישה ליומן…' },
        { at: 2800, label: 'שולחים לך אישור במייל ובוואטסאפ…' },
        { at: 5000, label: 'עוד רגע, כמעט שם…' },
      ]
    : [
        { at: 0, label: 'Holding your slot…' },
        { at: 1500, label: 'Adding the meeting to the calendar…' },
        { at: 2800, label: 'Sending your confirmation…' },
        { at: 5000, label: 'Almost there…' },
      ]

  const chosenSlotLabel =
    selectedDate && selectedTime
      ? `${format(selectedDate, 'dd/MM/yyyy')} · ${selectedTime}`
      : undefined

  const slotTakenText = isHe
    ? 'השעה הזו נתפסה ברגע האחרון. בחרו שעה אחרת - הרשימה מעודכנת.'
    : 'That slot was just taken. Pick another time - the list is up to date.'

  const availabilityErrorText = isHe
    ? 'לא הצלחנו לטעון את הזמינות כרגע.'
    : "We couldn't load the availability right now."
  const retryText = isHe ? 'נסו שוב' : 'Try again'
  const whatsappFallbackText = isHe ? 'או קבעו דרך וואטסאפ' : 'or book via WhatsApp'
  const whatsappFallbackHref =
    'https://wa.me/972545146464?text=' +
    encodeURIComponent(isHe ? 'היי, אשמח לקבוע פגישת ייעוץ' : 'Hi, I would like to book a consultation')

  const isLandingPage = initialSubject === BookingSubject.LANDING_PAGE
  const outerClasses = isLandingPage
    ? ''
    : embedded
      ? 'bg-gradient-to-br from-[#1a1a2e] via-[#2d1b69] to-[#0f0f23] rounded-3xl p-4 sm:p-8'
      : 'bg-gradient-to-br from-[#1a1a2e] via-[#2d1b69] to-[#0f0f23] min-h-screen p-5 sm:p-12'
  const cardMargin = !isLandingPage && !embedded ? 'sm:mt-10 mt-16' : ''

  return (
    <div className={`flex flex-col justify-center items-center ${outerClasses}`}>
      {isBooking && (
        <BookingProgress
          isRtl={isHe}
          steps={bookingSteps}
          title={isHe ? 'רגע אחד…' : 'One moment…'}
          detail={chosenSlotLabel}
          footnote={isHe ? 'אל תסגרו את החלון' : 'Please keep this window open'}
        />
      )}

      <Card className={`sm:w-full w-[23.3rem] max-w-7xl p-6 sm:p-8 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl ${cardMargin}`}>
        <h1 className="text-[1.5rem] sm:text-3xl font-bold mb-2 text-center sm:text-start text-white">
          {meetingType === BookingSubject.DOC_BRAIN_AI
            ? docbrainTitle
            : meetingType === BookingSubject.LANDING_PAGE && leadData?.challenge
              ? `${consultationTitle} - ${leadData.challenge}`
              : consultationTitle}
        </h1>
        <p className="sm:text-sm text-[1rem] text-gray-400 mb-6 text-center sm:text-start">
          {subtitle}
        </p>

        <div className="flex flex-col justify-center lg:flex-row gap-6 sm:gap-10">
          <div className="flex flex-col lg:w-[60%] gap-4 sm:gap-6">
            <div className="w-full">
              <p className="flex justify-center sm:justify-start font-bold text-[1rem] sm:text-sm mb-2 text-white">{selectDateTime}</p>
              <p className="flex  sm:justify-start text-[0.8rem] sm:text-xs mb-2 text-gray-400">{timeZone}</p>
              <div dir="ltr" className="flex justify-end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={onDateChosen}
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center items-center sm:justify-start sm:items-start sm:flex-row sm:gap-2">

            {selectedDate && (
              <div className="flex flex-col justify-center items-center sm:justify-start sm:items-start">
                <p className="text-[0.8rem] sm:text-xs font-semibold mb-2 text-white">
                  {format(selectedDate, "PPP")}
                </p>

                {/* Shown when the server reports the chosen slot was taken.
                    The lead stays on the picker with a fresh list instead of
                    being sent to the booking-failed dead end. */}
                {slotTakenMessage && (
                  <p
                    role="alert"
                    dir={isHe ? 'rtl' : 'ltr'}
                    className="mb-3 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[0.8rem] text-amber-200 max-w-xs"
                  >
                    {slotTakenMessage}
                  </p>
                )}

                <div className="relative">
                  {isLoading ? (
                        <div
                          key={1}
                          className="px-20 py-8 flex items-center justify-center mx-auto"
                        >
                          <Loader type="light" />
                        </div>
                  ) : availabilityError ? (
                    /* A failed lookup is NOT "no availability" - give the
                       lead a way forward instead of a dead end. */
                    <div
                      role="alert"
                      dir={isHe ? 'rtl' : 'ltr'}
                      className="flex flex-col items-start gap-3 max-w-xs"
                    >
                      <p className="text-[0.8rem] sm:text-xs text-amber-200">
                        {availabilityErrorText}
                      </p>
                      <div className="flex flex-wrap items-center gap-3">
                        <Button
                          onClick={() => refreshAvailableTimes()}
                          className="bg-[#998BFD] hover:bg-[#8073e6] text-white border-0"
                        >
                          {retryText}
                        </Button>
                        <a
                          href={whatsappFallbackHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[0.8rem] sm:text-xs text-[#998BFD] hover:text-[#b8abfd] underline"
                        >
                          {whatsappFallbackText}
                        </a>
                      </div>
                    </div>
                  ) : selectedDate.getDay() === 5 ||
                  selectedDate.getDay() === 6 ||
                  availableTimes.length === 0 ? (
                    <div className="flex flex-col items-start gap-2">
                      <p className="text-[0.8rem] sm:text-xs text-gray-400">
                        {noAvailability} {format(selectedDate, "PPP")}
                      </p>
                      {nextAvailable && (
                        <Button
                          onClick={() => onDateChosen(new Date(nextAvailable))}
                          className="bg-[#998BFD] hover:bg-[#8073e6] text-white border-0"
                        >
                          {checkNextAvailability}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 ">
                      {availableTimes.map((time) => (
                        <Button
                          key={time}
                          className={`text-sm font-semibold px-12 py-1 rounded-lg transition-all ${
                            selectedTime === time 
                              ? "bg-[#998BFD] text-white border border-[#998BFD] shadow-lg shadow-purple-500/20" 
                              : "bg-white/10 text-white border border-white/10 hover:border-white/20"
                          }`}
                          size="lg"
                          variant="ghost"
                          onClick={() => onTimeChosen(time)}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>

          <div className="flex-1 lg:w-[40%] sm:mr-10">
            {!leadData && <p className="text-[1.5rem] sm:text-xl font-bold mb-4 text-white">{clientDetailsTitle}</p>}
            <ClientDetailsForm
              dict={dict}
              onSubmit={handleSubmitForm}
              labels={labels}
              showMessage={meetingType !== BookingSubject.LANDING_PAGE}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              bookingDetailsTitle={bookingDetailsTitle}
              availableOnline={availableOnline}
              onlineMeeting={onlineMeeting}
              duration={duration}
              isSubmitting={isBooking}
              prefilledData={leadData ? {
                name: leadData.firstName,
                email: leadData.email,
                phone: leadData.phone
              } : undefined}
            />
          </div>
        </div>
      </Card>
    </div>
  )
}
